import React, { useCallback, useState } from 'react';
import AlertDialog from '../../../components/AlertDialog/AlertDialog';

interface Pending {
  title: string;
  message: string;
  confirmText: string;
  resolve: (ok: boolean) => void;
}

/**
 * Promise-based wrapper over the platform's centered AlertDialog, so the SP Form Templates
 * screens confirm destructive actions the same way the rest of the app does instead of
 * using the browser's window.confirm.
 *
 *   const { confirm, dialog } = useConfirm();
 *   if (await confirm('Activate?', 'Investors get this form from now on.', 'Activate')) …
 *   return <>{dialog}…</>
 */
export function useConfirm() {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback((title: string, message: string, confirmText = 'OK') =>
    new Promise<boolean>((resolve) => setPending({ title, message, confirmText, resolve })), []);

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const dialog = (
    <AlertDialog
      show={pending !== null}
      title={pending?.title ?? ''}
      message={pending?.message ?? ''}
      confirmText={pending?.confirmText ?? 'OK'}
      onConfirm={() => close(true)}
      onClose={() => close(false)}
    />
  );

  return { confirm, dialog };
}
